import { Context } from '../context/Context';
import { FullscreenQuad } from './FullscreenQuad';
import { RenderTexture, createColorTexture, createFramebuffer } from './RenderTarget';

export class FxaaPass {
  private fbo!: WebGLFramebuffer;
  private texture!: RenderTexture;
  private width!: number;
  private height!: number;

  init(ctx: Context): void {
    const gl = ctx.gl;
    this.width = ctx.viewPort.width;
    this.height = ctx.viewPort.height;

    // LDR resolve target — receives the tonemapped output of FinalPresentPass
    // (a depth-test-disabled fullscreen blit) and is then sampled by FXAA
    // itself. No pass writes gl_FragDepth here, so no depth attachment is
    // needed.
    this.texture = new RenderTexture();
    this.texture.glTexture = createColorTexture(
      gl,
      this.width,
      this.height,
      gl.RGBA8,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
    );
    this.fbo = createFramebuffer(gl, this.texture.glTexture!);
  }

  getFramebuffer(): WebGLFramebuffer {
    return this.fbo;
  }

  getTexture(): RenderTexture {
    return this.texture;
  }

  render(ctx: Context, quad: FullscreenQuad, outputBuffer: WebGLFramebuffer | null = null): void {
    const gl = ctx.gl;

    // FXAA reads from own texture (scene was rendered into this.fbo),
    // writes anti-aliased result to the output buffer.
    gl.bindFramebuffer(gl.FRAMEBUFFER, outputBuffer);
    gl.viewport(0, 0, ctx.viewPort.width, ctx.viewPort.height);
    gl.disable(gl.DEPTH_TEST);
    gl.disable(gl.BLEND);

    const shader = ctx.engine.shaderManager.getFxaaShader();
    ctx.setShader(shader);

    shader.uniformsContainer
      .setInputTexture(this.texture)
      .setInverseResolution([1.0 / this.width, 1.0 / this.height]);

    shader.validate();
    quad.draw(gl);
  }

  destroy(gl: WebGL2RenderingContext): void {
    gl.deleteFramebuffer(this.fbo);
    if (this.texture.glTexture) gl.deleteTexture(this.texture.glTexture);
  }
}
